import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';
import { updateAddToCartPriceLabels } from '@theme/add-to-cart-price';

/**
 * Quantity calculator component for calculating coverage and units.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} sqFtInput
 * @property {HTMLInputElement} unitsInput
 * @property {HTMLElement} totalUnitsDisplay
 * @property {HTMLElement} totalCoverageDisplay
 * @property {HTMLElement} [sqFtLabel]
 * @property {HTMLElement} [unitsLabel]
 * @property {HTMLElement} [overageDropdown]
 * @property {HTMLButtonElement} [dropdownTrigger]
 * @property {HTMLElement} [dropdownMenu]
 * @property {HTMLElement} [dropdownValue]
 * @property {HTMLElement} [overageDisplay]
 *
 * @extends {Component<Refs>}
 */
class QuantityCalculator extends Component {
  /**
   * Required refs for this component.
   */
  // requiredRefs = ['sqFtInput', 'unitsInput', 'totalUnitsDisplay', 'totalCoverageDisplay'];

  /**
   * Coverage value per unit (from product metafields).
   * @type {number}
   */
  covers = 0;

  /**
   * Overage percentage (default 0%).
   * @type {number}
   */
  overage = 0;

  /**
   * Selling unit name (singular).
   * @type {string}
   */
  sellingUnit = '';

  /**
   * Maximum quantity available.
   * @type {number}
   */
  maxQuantity = 0;

  /**
   * External error message element (outside component).
   * @type {HTMLElement | null}
   */
  errorElement = null;

  /**
   * Flag to prevent recursive updates between inputs.
   * @type {boolean}
   */
  #isUpdating = false;

  /**
   * Store original sq ft value (without overage) for recalculation.
   * @type {number}
   */
  #originalSqFt = 0;

  /**
   * Variant update listener so the button total tracks the new box price.
   * @type {((event: Event) => void) | null}
   */
  #variantUpdateHandler = null;

  /**
   * Observer for third-party quantity prefills (MinMaxify).
   * @type {MutationObserver | null}
   */
  #externalQtyObserver = null;

  /**
   * Poll id for third-party quantity prefills.
   * @type {ReturnType<typeof setInterval> | null}
   */
  #externalQtyPollId = null;

  connectedCallback() {
    super.connectedCallback();
    
    // Wait for refs to be initialized
    requestIdleCallback(() => {
      this.#init();
    });
  }

  /**
   * Initialize the calculator after refs are ready
   */
  #init() {
    // Check if refs exist
    if (!this.refs.sqFtInput) {
      return;
    }
    
    const sqFtInput = /** @type {HTMLInputElement} */ (this.refs.sqFtInput);
    const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);
    
    this.covers = parseFloat(sqFtInput?.dataset?.covers || '0');
    this.sellingUnit = unitsInput?.placeholder?.toLowerCase() || 'unit';
    this.maxQuantity = parseFloat(this.dataset?.maxQuantity || '0');
    
    // Find error message element that's next to this component
    this.errorElement = this.nextElementSibling;
    if (this.errorElement && !this.errorElement.classList.contains('quantity-calculator__error')) {
      this.errorElement = null;
    }
    
    sqFtInput.addEventListener('input', (e) => {
      if (this.#isUpdating) return;
      this.#validateAndFormatInput(e);
      this.handleSqFtChange(e);
    });
    
    unitsInput.addEventListener('input', (e) => {
      if (this.#isUpdating) return;
      this.#validateAndFormatInput(e, false);
      this.handleUnitsChange(e);
    });
    
    
    this.#setupDropdown();
    
    this.clearCalculations();
    this.#listenForVariantUpdates();
    this.#watchExternalQuantityPrefill();
  }

  /**
   * After a variant morph, keep the current quantity and refresh the button total.
   */
  #listenForVariantUpdates() {
    const section = this.closest('.shopify-section') || document;
    this.#variantUpdateHandler = () => {
      requestAnimationFrame(() => this.#recalculateFromInputs());
    };
    section.addEventListener('variant:update', this.#variantUpdateHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#stopExternalQuantityWatch();
    if (this.#variantUpdateHandler) {
      const section = this.closest('.shopify-section') || document;
      section.removeEventListener('variant:update', this.#variantUpdateHandler);
      document.removeEventListener('variant:update', this.#variantUpdateHandler);
      this.#variantUpdateHandler = null;
    }
  }

  /**
   * MinMaxify writes "1" into Boxes after load without a shopper input.
   * Clear that prefill so Boxes, sticky qty, and the plain main label stay
   * aligned with "hide until a quantity is entered". Stop once the shopper
   * edits a calculator field. Clearing is less risky than treating the app
   * default as a chosen qty, which would change Boldara's passing on-load
   * label and show a sticky total before the shopper typed anything.
   */
  #watchExternalQuantityPrefill() {
    const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);
    const sqFtInput = /** @type {HTMLInputElement} */ (this.refs.sqFtInput);
    if (!unitsInput) return;

    let shopperEdited = false;
    const markShopperEdited = () => {
      if (this.#isUpdating) return;
      shopperEdited = true;
      this.#stopExternalQuantityWatch();
    };

    unitsInput.addEventListener('input', markShopperEdited);
    sqFtInput?.addEventListener('input', markShopperEdited);

    const clearPrefill = () => {
      if (shopperEdited || this.#isUpdating) return;
      if (unitsInput.value !== '1') return;
      if (sqFtInput?.value && sqFtInput.value !== '0') return;

      this.#isUpdating = true;
      unitsInput.value = '';
      unitsInput.removeAttribute('value');
      unitsInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.#isUpdating = false;
      this.clearCalculations();
    };

    clearPrefill();
    if (!unitsInput.isConnected) return;

    this.#externalQtyObserver = new MutationObserver(clearPrefill);
    this.#externalQtyObserver.observe(unitsInput, {
      attributes: true,
      attributeFilter: ['value', 'min'],
    });

    this.#externalQtyPollId = setInterval(() => {
      if (shopperEdited) {
        this.#stopExternalQuantityWatch();
        return;
      }
      clearPrefill();
    }, 250);
  }

  #stopExternalQuantityWatch() {
    this.#externalQtyObserver?.disconnect();
    this.#externalQtyObserver = null;
    if (this.#externalQtyPollId != null) {
      clearInterval(this.#externalQtyPollId);
      this.#externalQtyPollId = null;
    }
  }

  /**
   * Setup dropdown functionality
   */
  #setupDropdown() {
    const { dropdownTrigger, dropdownMenu, overageDropdown } = this.refs;
    
    if (!dropdownTrigger || !dropdownMenu) return;
    
    dropdownTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#toggleDropdown();
    });
    
    const items = dropdownMenu.querySelectorAll('.quantity-calculator__dropdown-item');
    items.forEach(item => {
      item.addEventListener('click', (e) => {
        const button = /** @type {HTMLButtonElement} */ (e.currentTarget);
        const value = button.dataset.value || '0';
        const text = button.textContent || 'No overage';
        
        this.#selectOption(value, text);
        this.#closeDropdown();
      });
    });
    
    document.addEventListener('click', (e) => {
      if (overageDropdown && !overageDropdown.contains(/** @type {Node} */ (e.target))) {
        this.#closeDropdown();
      }
    });
    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.#closeDropdown();
      }
    });
  }

  /**
   * Toggle dropdown open/close
   */
  #toggleDropdown() {
    const { dropdownTrigger, dropdownMenu } = this.refs;
    if (!dropdownTrigger || !dropdownMenu) return;
    
    const isExpanded = dropdownTrigger.getAttribute('aria-expanded') === 'true';
    
    if (isExpanded) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  }

  /**
   * Open dropdown
   */
  #openDropdown() {
    const { dropdownTrigger, dropdownMenu } = this.refs;
    if (!dropdownTrigger || !dropdownMenu) return;
    
    dropdownTrigger.setAttribute('aria-expanded', 'true');
    dropdownMenu.setAttribute('aria-hidden', 'false');
  }

  /**
   * Close dropdown
   */
  #closeDropdown() {
    const { dropdownTrigger, dropdownMenu } = this.refs;
    if (!dropdownTrigger || !dropdownMenu) return;
    
    dropdownTrigger.setAttribute('aria-expanded', 'false');
    dropdownMenu.setAttribute('aria-hidden', 'true');
  }

  /**
   * Select dropdown option
   * @param {string} value
   * @param {string} text
   */
  #selectOption(value, text) {
    const { dropdownValue, dropdownMenu } = this.refs;
    
    if (dropdownValue) {
      dropdownValue.textContent = text;
    }
    
    if (dropdownMenu) {
      const items = dropdownMenu.querySelectorAll('.quantity-calculator__dropdown-item');
      items.forEach(item => {
        const button = /** @type {HTMLButtonElement} */ (item);
        if (button.dataset.value === value) {
          button.setAttribute('aria-selected', 'true');
        } else {
          button.removeAttribute('aria-selected');
        }
      });
    }
    
    this.setOverage(parseFloat(value));
  }

  /**
   * Get plural or singular form of selling unit
   * @param {number} count
   * @returns {string}
   */
  #getUnitText(count) {
    if (count === 1) {
      if (this.sellingUnit.endsWith('xes')) {
        return this.sellingUnit.slice(0, -2);
      } else if (this.sellingUnit.endsWith('s')) {
        return this.sellingUnit.slice(0, -1);
      }
      return this.sellingUnit;
    } else {
      if (this.sellingUnit.endsWith('x')) {
        return this.sellingUnit + 'es';
      } else if (this.sellingUnit.endsWith('s')) {
        return this.sellingUnit;
      } else {
        return this.sellingUnit + 's';
      }
    }
  }

  /**
   * Validate and format input value
   * @param {Event} event
   * @param {boolean} allowDecimals - Allow decimal numbers
   */
  #validateAndFormatInput(event, allowDecimals = true) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    let value = input.value;
    
    value = value.replace(/,/g, '.');
    
    if (allowDecimals) {
      const cleaned = value.replace(/[^0-9.]/g, '');
      
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      } else if (parts.length === 2 && parts[1] && parts[1].length > 4) {
        value = parts[0] + '.' + parts[1].substring(0, 4);
      } else {
        value = cleaned;
      }
    } else {
      value = value.replace(/[^0-9]/g, '');
    }
    
    if (input.value !== value) {
      input.value = value;
    }
  }

  updatedCallback() {
    super.updatedCallback?.();
    
    const sqFtInput = /** @type {HTMLInputElement} */ (this.refs.sqFtInput);
    this.covers = parseFloat(sqFtInput?.dataset?.covers || '0');
    
    this.#recalculateFromInputs();
  }

  /**
   * Recalculate from whatever the shopper already typed (used on variant change).
   */
  #recalculateFromInputs() {
    const sqFtInput = /** @type {HTMLInputElement} */ (this.refs.sqFtInput);
    const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);

    if (unitsInput?.value && unitsInput.value !== '0') {
      this.handleUnitsChange(/** @type {Event} */ (/** @type {unknown} */ ({ target: unitsInput })));
    } else if (sqFtInput?.value && sqFtInput.value !== '0') {
      this.handleSqFtChange(/** @type {Event} */ (/** @type {unknown} */ ({ target: sqFtInput })));
    } else {
      this.clearCalculations();
    }
  }

  /**
   * Handle square feet input change.
   * @param {Event} event
   */
  handleSqFtChange(event) {
    const inputValue = /** @type {HTMLInputElement} */ (event.target).value;
    const sqFtValue = parseFloat(inputValue) || 0;
    if (sqFtValue === 0 || inputValue === '' || inputValue === '0') {
      this.clearCalculations();
      const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);
      const sqFtInput = /** @type {HTMLInputElement} */ (this.refs.sqFtInput);
      this.#isUpdating = true;
      unitsInput.value = '';
      sqFtInput.value = '';
      unitsInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.#isUpdating = false;
      this.#originalSqFt = 0;
      return;
    }
    
    const originalSqFt = this.overage > 0 
      ? sqFtValue / (1 + this.overage / 100)
      : sqFtValue;
    
    this.#originalSqFt = originalSqFt;
    
    const sqFtWithOverage = sqFtValue;

    // Calculate units needed based on sq ft with overage
    const unitsNeeded = this.covers > 0
      ? Math.ceil(sqFtWithOverage / this.covers)
      : 0;

    // Update units input
    const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);
    const newUnitsValue = String(unitsNeeded);
    
    this.#isUpdating = true;
    unitsInput.value = newUnitsValue;
    unitsInput.setAttribute('value', newUnitsValue);
    
    // Dispatch input event so other components (like sticky ATC) can sync
    unitsInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.#isUpdating = false;

    // Update units label text
    if (this.refs.unitsLabel) {
      this.refs.unitsLabel.textContent = this.#getUnitText(unitsNeeded);
    }
    
    const totalCoverage = unitsNeeded * this.covers;
    
    // Validate against max quantity
    this.#validateQuantity(unitsNeeded);
    
    this.updateDisplays(unitsNeeded, totalCoverage, originalSqFt, sqFtWithOverage);
  }

  /**
   * Handle units input change.
   * @param {Event} event
   */
  handleUnitsChange(event) {
    const inputValue = /** @type {HTMLInputElement} */ (event.target).value;
    const unitsValue = parseFloat(inputValue) || 0;

    // Update label text
    if (this.refs.unitsLabel && inputValue && inputValue !== '0') {
      this.refs.unitsLabel.textContent = this.#getUnitText(unitsValue);
    }

    if (unitsValue === 0 || inputValue === '' || inputValue === '0') {
      this.clearCalculations();
      const sqFtInput = /** @type {HTMLInputElement} */ (this.refs.sqFtInput);
      const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);
      this.#isUpdating = true;
      sqFtInput.value = '';
      unitsInput.value = '';
      unitsInput.dispatchEvent(new Event('input', { bubbles: true }));
      this.#isUpdating = false;
      this.#originalSqFt = 0;
      return;
    }
    
    const totalCoverage = unitsValue * this.covers;
    
    const originalSqFt = this.overage > 0 
      ? totalCoverage / (1 + this.overage / 100)
      : totalCoverage;
    
    const sqFtWithOverage = totalCoverage;
    
    this.#originalSqFt = originalSqFt;
    
    const sqFtInput = /** @type {HTMLInputElement} */ (this.refs.sqFtInput);
    const newValue = String(Math.round(sqFtWithOverage * 100) / 100);
    
    this.#isUpdating = true;
    sqFtInput.value = newValue;
    sqFtInput.setAttribute('value', newValue);
    this.#isUpdating = false;
    
    // Validate against max quantity
    this.#validateQuantity(unitsValue);
    
    this.updateDisplays(unitsValue, totalCoverage, originalSqFt, sqFtWithOverage);
  }

  /**
   * Validate quantity against max available inventory
   * @param {number} units
   */
  #validateQuantity(units) {
    if (!this.errorElement) return;
    
    if (this.maxQuantity > 0 && units > this.maxQuantity) {
      this.errorElement.style.display = 'block';
    } else {
      this.errorElement.style.display = 'none';
    }
  }

  /**
   * Update add-to-cart button price based on quantity
   * @param {number} units
   */
  #updateAddToCartPrice(units) {
    updateAddToCartPriceLabels(units);
  }

  /**
   * Update the display elements.
   * @param {number} units
   * @param {number} coverage
   * @param {number} originalSqFt - Original square footage before overage
   * @param {number} sqFtWithOverage - Square footage with overage applied
   */
  updateDisplays(units, coverage, originalSqFt, sqFtWithOverage) {
    const unitText = this.#getUnitText(units);
    const coversText = this.covers > 0 ? ` (${this.covers} sq.ft each)` : '';
    const totalUnitsDisplay = /** @type {HTMLElement} */ (this.refs.totalUnitsDisplay);
    totalUnitsDisplay.textContent = `${units} ${unitText}${coversText}`;
    
    const totalCoverageDisplay = /** @type {HTMLElement} */ (this.refs.totalCoverageDisplay);
    totalCoverageDisplay.textContent = `${Math.round(coverage * 100) / 100} sq.ft`;
    
    if (this.refs.overageDisplay) {
      if (this.overage > 0 && sqFtWithOverage) {
        const overageSqFt = sqFtWithOverage - originalSqFt;
        this.refs.overageDisplay.textContent = `${this.overage}% (+${Math.round(overageSqFt * 100) / 100} sq.ft)`;
      } else {
        this.refs.overageDisplay.textContent = 'No overage';
      }
    }
    
    this.#updateAddToCartPrice(units);
  }

  /**
   * Clear all calculations and reset displays.
   */
  clearCalculations() {
    const unitText = this.#getUnitText(0);
    const coversText = this.covers > 0 ? ` (${this.covers} sq.ft each)` : '';
    const totalUnitsDisplay = /** @type {HTMLElement} */ (this.refs.totalUnitsDisplay);
    totalUnitsDisplay.textContent = `0 ${unitText}${coversText}`;
    
    const totalCoverageDisplay = /** @type {HTMLElement} */ (this.refs.totalCoverageDisplay);
    totalCoverageDisplay.textContent = '0 sq.ft';
    
    // Hide error message
    if (this.errorElement) {
      this.errorElement.style.display = 'none';
    }
    
    this.#updateAddToCartPrice(0);
  }

  /**
   * Set overage percentage and recalculate.
   * @param {number} percentage
   */
  setOverage(percentage) {
    this.overage = parseFloat(String(percentage)) || 0;
    
    const sqFtInput = /** @type {HTMLInputElement} */ (this.refs.sqFtInput);
    const unitsInput = /** @type {HTMLInputElement} */ (this.refs.unitsInput);
    
    if (this.#originalSqFt > 0) {
      // Apply new overage to stored original value
      const sqFtWithOverage = this.#originalSqFt * (1 + this.overage / 100);

      // Calculate units needed
      const unitsNeeded = this.covers > 0
        ? Math.ceil(sqFtWithOverage / this.covers)
        : 0;

      // Update both inputs
      this.#isUpdating = true;

      // Update sq ft input to show new value WITH overage
      const newSqFtValue = String(Math.round(sqFtWithOverage * 100) / 100);
      sqFtInput.value = newSqFtValue;
      sqFtInput.setAttribute('value', newSqFtValue);

      // Update units
      const newUnitsValue = String(unitsNeeded);
      unitsInput.value = newUnitsValue;
      unitsInput.setAttribute('value', newUnitsValue);

      this.#isUpdating = false;

      // Dispatch input event so other components (like sticky ATC) can sync
      unitsInput.dispatchEvent(new Event('input', { bubbles: true }));
      
      if (this.refs.unitsLabel) {
        this.refs.unitsLabel.textContent = this.#getUnitText(unitsNeeded);
      }
      
      const totalCoverage = unitsNeeded * this.covers;
      this.updateDisplays(unitsNeeded, totalCoverage, this.#originalSqFt, sqFtWithOverage);
    } else if (sqFtInput?.value) {
      const mockEvent = { target: sqFtInput };
      this.handleSqFtChange(/** @type {Event} */ (/** @type {unknown} */ (mockEvent)));
    } else if (unitsInput?.value) {
      const mockEvent = { target: unitsInput };
      this.handleUnitsChange(/** @type {Event} */ (/** @type {unknown} */ (mockEvent)));
    }
  }
}

if (!customElements.get('quantity-calculator')) {
  customElements.define('quantity-calculator', QuantityCalculator);
}
